import React, { FC } from "react";
import { useSelector } from "react-redux";
import { ColumnDescriptionDc } from "@evergis/api";
import { useDialogShadow, Icon, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { dialogResourceSelector } from "../../../../../ducks/dialogs/selectors";
import { getGeometryType } from "../../../../../evergis/core/attributes/geometryTypes";
import { useTablePreview } from "../../../../../hooks/preview/usePreview";
import { useTable } from "../../../../../hooks/table/useTable";
import { formatDate } from "../../../../../utils/date";
import { formatNumber } from "../../../../../utils/formatNumber";
import { Attributes } from "../../../DialogComponents/Attributes/Attributes";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { AttributeItem } from "./AttributeItem";

export const TableInfo: FC = () => {
  const { t } = useTranslation();
  const currentResource = useSelector(dialogResourceSelector);
  const { table } = useTable(currentResource);
  const { alias, changedDate, preview, description, owner, geometryType, columns, objectsCount, name } = table;
  const [previewUrl] = useTablePreview(name);
  const { shadows, setScrollContainer } = useDialogShadow();

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          icon: "info",
        },
        applyText: t("actions.ok", { ns: "common" }),
        attributes: {
          label: t("infoResource.data", { ns: "dialogs" }),
          description: t("infoResource.dataDescription", { ns: "dialogs" }),
          icon: "data",
        },
      }}
    >
      <DialogTitle
        displayShadow={shadows.upper}
        titleText={styledHeaderText(alias, t("properties", { ns: "common" }))}
      />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <ProjectMetaBlock>
            <ResourceMeta meta={{ description, preview: { src: preview ? previewUrl : "" } }} readOnly>
              <DataField column>
                <FieldValue field={t("infoResource.owner", { ns: "dialogs" })} value={owner} />
                <DataField style={{ marginBottom: "1rem" }}>
                  <FieldValue
                    field={t("infoResource.geometryType", { ns: "dialogs" })}
                    value={
                      <>
                        {geometryType && <Icon kind={`geom_${geometryType}` as IconTypesKeys} />}
                        <span>{t(getGeometryType(geometryType), { ns: "common" })}</span>
                      </>
                    }
                  />
                  <FieldValue
                    field={t("infoResource.totalFeatures", { ns: "dialogs" })}
                    value={formatNumber(objectsCount)}
                  />
                </DataField>
                <FieldValue field={t("infoResource.tableUpdated", { ns: "dialogs" })} value={formatDate(changedDate)} />
              </DataField>
            </ResourceMeta>
          </ProjectMetaBlock>
        </RestrictedWidthContainer>
        <Attributes attributes={columns} draggable={false}>
          {(attribute: ColumnDescriptionDc) => <AttributeItem attribute={attribute} />}
        </Attributes>
      </DialogContent>
      <DialogActions displayShadow={shadows.bottom} />
    </FullScreenDialog>
  );
};
